import React, { useState, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { format, addDays } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { X } from 'lucide-react';

export default function StaffExtraBooking() {
  const { user } = useAuth();
  const [masters, setMasters] = useState([]);
  const [selectedMaster, setSelectedMaster] = useState('');
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedPlace, setSelectedPlace] = useState('');
  const [studentName, setStudentName] = useState('');
  const [note, setNote] = useState('');
  const [times, setTimes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [occupiedSlots, setOccupiedSlots] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const today = new Date();
  const maxDate = addDays(today, 30);

  // Загрузка данных
  useEffect(() => {
    Promise.all([
      fetch('/api/mastercars').then(r => r.json()),
      fetch('/api/times').then(r => r.json()),
      fetch('/api/locations').then(r => r.json())
    ]).then(([mastersData, timesData, locData]) => {
      setMasters(mastersData);
      setTimes(timesData);
      setLocations(locData);
    }).catch(err => console.error('Ошибка загрузки данных:', err));
  }, []);

  // Загрузка занятых слотов
  useEffect(() => {
    if (selectedMaster && selectedDate) {
      fetch(`/api/schedule/${selectedMaster}`)
        .then(r => r.ok ? r.json() : [])
        .then(slots => {
          const dateStr = format(selectedDate, 'yyyy-MM-dd');
          setOccupiedSlots(slots.filter(s => s.date === dateStr));
        })
        .catch(() => setOccupiedSlots([]));
    }
  }, [selectedMaster, selectedDate]);

  const availableTimes = times.filter(time => {
    const dateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '';
    return !occupiedSlots.some(slot => slot.time === time && slot.date === dateStr);
  });

  const handleSubmit = async () => {
    if (!selectedMaster || !selectedDate || !selectedTime || !selectedPlace || !studentName.trim()) {
      setMessage('Пожалуйста, заполните все обязательные поля');
      return;
    }

    setLoading(true);
    setMessage('');

    const dateStr = format(selectedDate, 'yyyy-MM-dd');

    try {
      const res = await fetch('/api/add-booking/extra', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          instructorId: selectedMaster,
          date: dateStr,
          time: selectedTime,
          place: selectedPlace,
          studentName: studentName.trim(),
          note: note.trim(),
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setMessage('✅ Дополнительная запись успешно добавлена!');
        // Сброс формы
        setSelectedDate(undefined);
        setSelectedTime('');
        setSelectedPlace('');
        setStudentName('');
        setNote('');
        setOccupiedSlots([]);
      } else {
        setMessage(data.message || 'Ошибка при добавлении записи');
      }
    } catch (err) {
      setMessage('Ошибка соединения с сервером');
    } finally {
      setLoading(false);
    }
  };

  if (user?.status !== 'staff') {
    return <div className="text-red-500 text-center mt-20 text-2xl">Доступ запрещён</div>;
  }

  return (
    <div className="min-h-screen bg-[#1b1b1b] text-white pb-20">
      <Header />
      <div className="max-w-2xl mx-auto px-6 pt-24">
        <h1 className="text-4xl font-black uppercase text-center mb-12 tracking-tight">
          Дополнительная запись на практику
        </h1>

        <div className="glass-panel rounded-3xl p-8 border border-white/10">
          {/* Инструктор */}
          <div className="mb-6">
            <label className="block text-sm text-slate-400 mb-2">Инструктор</label>
            <select
              value={selectedMaster}
              onChange={e => setSelectedMaster(e.target.value)}
              className="w-full p-4 bg-white/5 border border-white/20 rounded-2xl text-white focus:border-primary focus:outline-none"
            >
              <option value="" className="bg-[#1b1b1b]">Выберите инструктора</option>
              {masters.map(m => (
                <option key={m.id} value={m.id} className="bg-[#1b1b1b]">
                  {m.fullName}
                </option>
              ))}
            </select>
          </div>

          {/* Календарь */}
          <div className="mb-6">
            <label className="block text-sm text-slate-400 mb-2">Дата занятия</label>
            <div className="bg-white/5 border border-white/20 rounded-3xl p-4">
              <DayPicker
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                locale={ru}
                fromDate={today}
                toDate={maxDate}
                disabled={{ before: today }}
                className="w-full"
              />
            </div>
          </div>

          {/* Время и место */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Время</label>
              <select
                value={selectedTime}
                onChange={e => setSelectedTime(e.target.value)}
                className="w-full p-4 bg-white/5 border border-white/20 rounded-2xl text-white focus:border-primary focus:outline-none"
              >
                <option value="" className="bg-[#1b1b1b]">Выберите время</option>
                {availableTimes.map(t => (
                  <option key={t} value={t} className="bg-[#1b1b1b]">{t}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-2">Место</label>
              <select
                value={selectedPlace}
                onChange={e => setSelectedPlace(e.target.value)}
                className="w-full p-4 bg-white/5 border border-white/20 rounded-2xl text-white focus:border-primary focus:outline-none"
              >
                <option value="" className="bg-[#1b1b1b]">Выберите место</option>
                {locations.map(loc => (
                  <option key={loc} value={loc} className="bg-[#1b1b1b]">{loc}</option>
                ))}
              </select>
            </div>
          </div>

          {/* ФИО ученика */}
          <div className="mb-6">
            <label className="block text-sm text-slate-400 mb-2">ФИО ученика или описание</label>
            <input
              type="text"
              value={studentName}
              onChange={e => setStudentName(e.target.value)}
              className="w-full p-4 bg-white/5 border border-white/20 rounded-2xl text-white placeholder:text-slate-500 focus:border-primary focus:outline-none"
              placeholder="Например: Иванов Иван Иванович"
            />
          </div>

          {/* Примечание */}
          <div className="mb-8">
            <label className="block text-sm text-slate-400 mb-2">Примечание (необязательно)</label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              className="w-full p-4 bg-white/5 border border-white/20 rounded-2xl text-white placeholder:text-slate-500 focus:border-primary focus:outline-none h-28 resize-y"
              placeholder="Дополнительная информация..."
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading || !selectedMaster || !selectedDate || !selectedTime || !selectedPlace || !studentName.trim()}
            className="w-full py-5 bg-primary hover:bg-primary/90 text-black font-black rounded-2xl text-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Записываем...' : 'Подтвердить дополнительную запись'}
          </button>

          {message && (
            <p className={`text-center mt-6 text-lg font-medium ${
              message.includes('✅') ? 'text-green-400' : 'text-red-400'
            }`}>
              {message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}